import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { TrackRow } from "../pages/Explore";
import { Loading, Empty, formatNum } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Play, ListMusic, Disc3, Plus, Bookmark, BookmarkCheck, Lock, Globe, Trash2, Headphones } from "lucide-react";
import { toast } from "sonner";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

// One dialog for a single song or a whole album: save to library, add to an existing or new playlist.
export function AddToPlaylistDialog({ track, album, onClose }) {
  const { user } = useAuth();
  const item = track || album;
  const kind = track ? "track" : "album";
  const [playlists, setPlaylists] = useState([]);
  const [saved, setSaved] = useState(false);
  const [name, setName] = useState("");
  useEffect(() => {
    if (!item || !user) return;
    api.get("/playlists").then(({ data }) => setPlaylists(data)).catch(() => {});
    api.get("/library").then(({ data }) => setSaved(data.saved_ids.includes(item.id))).catch(() => {});
  }, [item, user]);
  if (!item) return null;
  if (!user) { toast.error("Sign in to save music"); onClose(); return null; }
  const addTo = async (pid) => {
    if (track) await api.post(`/playlists/${pid}/tracks?track_id=${track.id}`);
    else { const { data } = await api.post(`/playlists/${pid}/album/${album.id}`); return data.added; }
  };
  const add = async (pid) => {
    try { const n = await addTo(pid); toast.success(album ? `Added ${plural(n, "song")} to playlist` : "Added to playlist"); onClose(); } catch (e) { toast.error(errText(e)); }
  };
  const createAdd = async () => {
    if (!name.trim()) return;
    try { const { data } = await api.post("/playlists", { name }); await add(data.id); } catch (e) { toast.error(errText(e)); }
  };
  const toggleSave = async () => {
    try { const { data } = await api.post("/library/toggle", { kind, ref_id: item.id }); setSaved(data.saved); toast.success(data.saved ? "Saved to your library" : "Removed from your library"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 sm:max-w-sm" data-testid="add-playlist-dialog">
        <DialogHeader><DialogTitle className="font-display">{album ? "Add entire album" : "Add song"}</DialogTitle><DialogDescription className="text-xs text-muted-foreground truncate">{item.title}</DialogDescription></DialogHeader>
        <Button onClick={toggleSave} variant="outline" className={`w-full rounded-xl border-white/15 ${saved ? "text-gold border-gold/40" : ""}`} data-testid="library-save-toggle">
          {saved ? <BookmarkCheck size={16} className="mr-2" /> : <Bookmark size={16} className="mr-2" />}{saved ? "Saved to library" : "Save to library"}
        </Button>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground pt-1">Add to playlist</div>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {playlists.map((p) => <button key={p.id} onClick={() => add(p.id)} className="w-full text-left surface-2 rounded-lg px-3 py-2.5 text-sm hover:border-eblue/40 border border-white/10 transition-colors" data-testid={`select-playlist-${p.id}`}>{p.name} <span className="text-xs text-muted-foreground">· {p.tracks_count}</span></button>)}
          {playlists.length === 0 && <p className="text-xs text-muted-foreground">No playlists yet. Create one below.</p>}
        </div>
        <div className="flex gap-2 pt-1"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New playlist" className="surface-2 border-white/10" data-testid="new-playlist-input" /><Button onClick={createAdd} className="btn-blue rounded-xl" data-testid="new-playlist-add-btn"><Plus size={16} /></Button></div>
      </DialogContent>
    </Dialog>
  );
}

export function AlbumGrid({ albums, onOpen, testid = "album-grid" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid={testid}>
      {albums.map((a) => (
        <button key={a.id} onClick={() => onOpen(a.id)} className="surface rounded-xl overflow-hidden card-hover text-left group" data-testid={`album-card-${a.id}`}>
          <div className="relative aspect-square bg-gradient-to-br from-eblue/20 to-gold/10 flex items-center justify-center">
            {a.cover_url ? <img src={a.cover_url} className="w-full h-full object-cover" alt="" /> : <Disc3 className="text-eblue w-10 h-10" />}
            <span className="absolute bottom-2 right-2 w-10 h-10 rounded-full btn-gold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Play size={18} /></span>
          </div>
          <div className="p-3">
            <div className="font-medium text-sm truncate">{a.title}</div>
            <div className="text-xs text-muted-foreground truncate">{a.artist?.display_name ? `${a.artist.display_name} · ` : ""}{a.type || "Album"} · {plural(a.tracks_count || 0, "track")}</div>
            <div className="text-xs text-gold font-mono mt-0.5 flex items-center gap-1" data-testid={`album-streams-${a.id}`}><Headphones size={12} />{formatNum(a.total_streams || 0)} streams</div>
          </div>
        </button>
      ))}
    </div>
  );
}

export function AlbumViewDialog({ albumId, onClose }) {
  const player = usePlayer();
  const [a, setA] = useState(null);
  const [addTrack, setAddTrack] = useState(null);
  const [addAlbum, setAddAlbum] = useState(null);
  useEffect(() => { setA(null); if (albumId) api.get(`/music/album/${albumId}`).then(({ data }) => setA(data)).catch(() => { toast.error("Album unavailable"); onClose(); }); }, [albumId]); // eslint-disable-line
  if (!albumId) return null;
  const playAll = () => a?.tracks?.length ? player.playTrack(a.tracks[0], a.tracks) : toast.error("Album has no tracks yet");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 sm:max-w-xl max-h-[88vh] overflow-y-auto" data-testid="album-view-dialog">
        <DialogHeader><DialogTitle className="sr-only">Album</DialogTitle><DialogDescription className="sr-only">Album tracks</DialogDescription></DialogHeader>
        {!a ? <Loading /> : <>
          <div className="flex gap-4 items-end">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl overflow-hidden shrink-0 bg-gradient-to-br from-eblue/20 to-gold/10 flex items-center justify-center">{a.cover_url ? <img src={a.cover_url} className="w-full h-full object-cover" alt="" /> : <Disc3 className="text-eblue w-10 h-10" />}</div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{a.type || "Album"}</div>
              <h3 className="font-display font-bold text-2xl truncate" data-testid="album-view-title">{a.title}</h3>
              {a.artist && <Link to={`/${a.artist.username}`} onClick={onClose} className="text-sm text-muted-foreground hover:text-eblue">{a.artist.display_name}</Link>}
              <div className="text-xs text-gold font-mono mt-1" data-testid="album-view-total-streams">{formatNum(a.total_streams || 0)} total streams · {plural(a.tracks_count || 0, "track")}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={playAll} className="btn-gold rounded-full" data-testid="album-play-all"><Play size={15} className="mr-1.5" />Play</Button>
            <Button onClick={() => setAddAlbum(a)} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="album-add-entire"><Plus size={15} className="mr-1.5" />Add entire album</Button>
          </div>
          <div className="space-y-2">
            {a.tracks.length === 0 ? <Empty icon={Disc3} title="No tracks yet" /> : a.tracks.map((t, i) => <TrackRow key={t.id} t={t} rank={i + 1} onPlay={() => player.playTrack(t, a.tracks)} onAdd={setAddTrack} />)}
          </div>
        </>}
        <AddToPlaylistDialog track={addTrack} onClose={() => setAddTrack(null)} />
        <AddToPlaylistDialog album={addAlbum} onClose={() => setAddAlbum(null)} />
      </DialogContent>
    </Dialog>
  );
}

export function PlaylistCard({ p, onOpen, onChanged, owner }) {
  const togglePrivacy = async (e) => {
    e.stopPropagation();
    try { await api.put(`/playlists/${p.id}`, { is_public: !p.is_public }); toast.success(p.is_public ? "Playlist is now private" : "Playlist is now public"); onChanged?.(); } catch (er) { toast.error(errText(er)); }
  };
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete playlist "${p.name}"?`)) return;
    try { await api.delete(`/playlists/${p.id}`); toast.success("Playlist deleted"); onChanged?.(); } catch (er) { toast.error(errText(er)); }
  };
  return (
    <div role="button" onClick={() => onOpen(p.id)} className="surface rounded-xl p-4 flex items-center gap-3 card-hover cursor-pointer" data-testid={`playlist-${p.id}`}>
      <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-eblue/30 to-gold/20 flex items-center justify-center overflow-hidden shrink-0">{p.cover_url ? <img src={p.cover_url} className="w-full h-full object-cover" alt="" /> : <ListMusic className="text-eblue" />}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{p.name}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-1">{p.is_public === false ? <Lock size={11} /> : <Globe size={11} />}{p.is_public === false ? "Private" : "Public"} · {plural(p.tracks_count || 0, "track")}</div>
      </div>
      {owner && <button onClick={togglePrivacy} className="p-2 text-muted-foreground hover:text-gold transition-colors" title={p.is_public === false ? "Make public" : "Make private"} data-testid={`playlist-privacy-${p.id}`}>{p.is_public === false ? <Lock size={16} /> : <Globe size={16} />}</button>}
      {owner && <button onClick={remove} className="p-2 text-muted-foreground hover:text-red-400 transition-colors" title="Delete playlist" data-testid={`playlist-delete-${p.id}`}><Trash2 size={16} /></button>}
    </div>
  );
}

export function PlaylistViewDialog({ playlistId, onClose, owner, onChanged }) {
  const player = usePlayer();
  const [p, setP] = useState(null);
  const [addTrack, setAddTrack] = useState(null);
  const load = () => api.get(`/playlists/${playlistId}`).then(({ data }) => setP(data)).catch(() => { toast.error("Playlist unavailable"); onClose(); });
  useEffect(() => { setP(null); if (playlistId) load(); }, [playlistId]); // eslint-disable-line
  if (!playlistId) return null;
  const removeTrack = async (tid) => { try { await api.delete(`/playlists/${playlistId}/tracks/${tid}`); load(); onChanged?.(); } catch (e) { toast.error(errText(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 sm:max-w-xl max-h-[88vh] overflow-y-auto" data-testid="playlist-view-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ListMusic size={18} className="text-eblue" />{p?.name || "Playlist"}</DialogTitle><DialogDescription className="text-xs text-muted-foreground">{p ? plural(p.tracks?.length || 0, "track") : ""}</DialogDescription></DialogHeader>
        {!p ? <Loading /> : <>
          <Button onClick={() => p.tracks.length ? player.playTrack(p.tracks[0], p.tracks) : toast.error("Playlist is empty")} className="btn-gold rounded-full w-fit" data-testid="playlist-play-all"><Play size={15} className="mr-1.5" />Play</Button>
          <div className="space-y-2">
            {p.tracks.length === 0 ? <Empty icon={ListMusic} title="Empty playlist" /> : p.tracks.map((t, i) => (
              <div key={t.id} className="flex items-center gap-1">
                <div className="flex-1 min-w-0"><TrackRow t={t} rank={i + 1} onPlay={() => player.playTrack(t, p.tracks)} onAdd={setAddTrack} /></div>
                {owner && <button onClick={() => removeTrack(t.id)} className="p-2 text-muted-foreground hover:text-red-400" title="Remove" data-testid={`playlist-remove-track-${t.id}`}><Trash2 size={15} /></button>}
              </div>
            ))}
          </div>
        </>}
        <AddToPlaylistDialog track={addTrack} onClose={() => setAddTrack(null)} />
      </DialogContent>
    </Dialog>
  );
}

// Public profile → Music tab: Songs / Albums / Playlists.
export function ProfileMusic({ u, isSelf, tracks, onPlay }) {
  const [sub, setSub] = useState("songs");
  const [albums, setAlbums] = useState(null);
  const [playlists, setPlaylists] = useState(null);
  const [openAlbum, setOpenAlbum] = useState(null);
  const [openPl, setOpenPl] = useState(null);
  const [addTrack, setAddTrack] = useState(null);
  const loadPl = () => api.get(`/users/${u.id}/playlists`).then(({ data }) => setPlaylists(data)).catch(() => setPlaylists([]));
  useEffect(() => {
    api.get(`/music/albums?artist_id=${u.id}`).then(({ data }) => setAlbums(isSelf ? data : data.filter((a) => a.tracks_count > 0))).catch(() => setAlbums([]));
    loadPl();
  }, [u.id]); // eslint-disable-line
  const subs = [["songs", "Songs"], ["albums", "Albums"], ["playlists", "Playlists"]];
  return (
    <div data-testid="profile-music">
      <div className="flex gap-2 mb-4">
        {subs.map(([k, l]) => <button key={k} onClick={() => setSub(k)} data-testid={`profile-music-tab-${k}`} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${sub === k ? "bg-white text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>{l}</button>)}
      </div>
      {sub === "songs" && (!tracks ? <Loading /> : tracks.length === 0 ? <Empty title="No music released" /> : <div className="space-y-2">{tracks.map((t, i) => <TrackRow key={t.id} t={{ ...t, artist: u }} rank={i + 1} onPlay={() => onPlay({ ...t, artist: u }, tracks.map((x) => ({ ...x, artist: u })))} onAdd={setAddTrack} />)}</div>)}
      {sub === "albums" && (!albums ? <Loading /> : albums.length === 0 ? <Empty icon={Disc3} title="No albums yet" /> : <AlbumGrid albums={albums.map((a) => ({ ...a, artist: a.artist || u }))} onOpen={setOpenAlbum} testid="profile-albums" />)}
      {sub === "playlists" && (!playlists ? <Loading /> : playlists.length === 0 ? <Empty icon={ListMusic} title="No public playlists" /> : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="profile-playlists">{playlists.map((p) => <PlaylistCard key={p.id} p={p} onOpen={setOpenPl} owner={isSelf} onChanged={loadPl} />)}</div>
      ))}
      <AlbumViewDialog albumId={openAlbum} onClose={() => setOpenAlbum(null)} />
      <PlaylistViewDialog playlistId={openPl} onClose={() => setOpenPl(null)} owner={isSelf} onChanged={loadPl} />
      <AddToPlaylistDialog track={addTrack} onClose={() => setAddTrack(null)} />
    </div>
  );
}
